import { StrictMode, useState } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Routes, Route } from "react-router-dom";

import { registerSW } from "virtual:pwa-register";

import "../excalidraw-app/sentry";

import { getRoomNumberFromUrl } from "./data/parseRoomNumber";

import { AuthProvider, useAuth } from "./AuthContext";

import ExcalidrawApp from "./App";
import CallbackPage from "./CallbackPage";
import ErrorPage from "./ErrorPage";

import RoomJoinModal from "./collab/RoomJoin";

import type { JSX } from "react";

window.__EXCALIDRAW_SHA__ = import.meta.env.VITE_APP_GIT_SHA;
const rootElement = document.getElementById("root")!;
const root = createRoot(rootElement);
registerSW();

/**
 * Environment variables for OAuth configuration:
 * - AUTHORIZE_URI: OAuth provider's authorization endpoint
 * - CLIENT_ID: Application's OAuth client ID
 * - REDIRECT_URI: Callback URL for OAuth response
 * - AUTH_ACTIVATED: Feature flag to enable/disable authentication
 */
const AUTHORIZE_URI = import.meta.env.VITE_APP_AUTHORIZE_URI;
const CLIENT_ID = import.meta.env.VITE_APP_CLIENT_ID;
const REDIRECT_URI = import.meta.env.VITE_APP_REDIRECT_URI;
const AUTH_ACTIVATED = import.meta.env.VITE_APP_AUTH_ACTIVATED;

/**
 * Protected route wrapper component.
 *
 * Handles authentication checks before rendering child routes.
 * Supports three access modes:
 * 1. Full authentication via OAuth
 * 2. Guest access for room/link joining (prompts for username)
 * 3. Automatic re-authentication on token expiration
 *
 * When AUTH_ACTIVATED is true:
 * - Checks if user is joining a room or shareable link
 * - Shows RoomJoinModal for guest/login choice
 * - Redirects to OAuth if token expired
 * - Saves return URL for post-auth redirect
 *
 * @param children - Protected components to render when authenticated
 */
const PrivateRoute = ({ children }: { children: JSX.Element }) => {
  const { isAuthenticated, isExpired, loginAsGuest } = useAuth();
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [username, setUsername] = useState("");
  const [joinedAsGuest, setJoinedAsGuest] = useState(false);

  if (AUTH_ACTIVATED) {
    const isJoiningRoom = /#room=/g.test(window.location.href);
    const isJoiningLink = /#json=/g.test(window.location.href);

    if (
      (isJoiningRoom || isJoiningLink) &&
      (!isAuthenticated || isExpired()) &&
      !joinedAsGuest
    ) {
      if (!showJoinModal) {
        setShowJoinModal(true);
      }

      return (
        <>
          {showJoinModal && (
            <RoomJoinModal
              username={username}
              setUsername={setUsername}
              onJoin={() => {
                if (username.trim()) {
                  loginAsGuest(username);
                  localStorage.setItem("name", username);
                  setJoinedAsGuest(true);
                  setShowJoinModal(false);
                }
              }}
              onLogIn={() => {
                localStorage.setItem(
                  "room_id",
                  getRoomNumberFromUrl(window.location.href),
                );
                window.location.href = `${AUTHORIZE_URI}?client_id=${CLIENT_ID}&redirect_uri=${REDIRECT_URI}&scope=openid profile email&response_type=code`;
                return null;
              }}
            />
          )}
        </>
      );
    } else if (isExpired() && !joinedAsGuest && !isJoiningRoom && !isJoiningLink) {
      window.location.href = `${AUTHORIZE_URI}?client_id=${CLIENT_ID}&redirect_uri=${REDIRECT_URI}&scope=openid profile email&response_type=code`;
      return null;
    }
  }

  return children;
};

const AppRoutes = () => {
  return (
    <Routes>
      <Route
        path="/"
        element={
          <PrivateRoute>
            <ExcalidrawApp />
          </PrivateRoute>
        }
      />
      <Route path="/callback" element={<CallbackPage />} />
      <Route path="/error" element={<ErrorPage />} />
    </Routes>
  );
};

root.render(
  <StrictMode>
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  </StrictMode>,
);
